import React, { useEffect, useState } from 'react'
import Layout from '../../components/Layout/Layout'
import { useParams } from 'react-router-dom';
import { convert } from '../../utils/convertDate';
import WakanowServices from '../../services/wakanowServices';
import BackButton from '../../components/BackButton';
import { FormatMoney } from '../../utils/formatMoney';

const statusColors = {
    'successful': 'bg-green-100 text-green-700',
    'initiated': 'bg-yellow-100 text-yellow-700',
    'failed': 'bg-red-100 text-red-700',
};

const ViewHotelBooking = () => {
    const { id } = useParams();
    const [data, setData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const fetchData = async () => {
            setIsLoading(true);
            try {
                const response = await WakanowServices.getHotelBookingById(id);
                if (response) {
                    setData(response.data);
                }
            } catch (error) {
            } finally {
                setIsLoading(false);
            }
        };
        fetchData();
    }, [id]);

    if (isLoading) {
        return (
            <Layout title="Hotel Booking" pageTag={"wakanow"} current={'/wakanow/hotel-bookings'}>
                <div className="min-h-full">
                    <div className="flex flex-1 flex-col">
                        <main className="flex-1">
                            <div className="py-6">
                                <div className="mx-auto max-w-7xl px-4 sm:px-6 md:px-8">
                                    <div className="bg-white shadow sm:rounded-lg">
                                        <div className="px-4 py-5 sm:p-6">
                                            <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-2">
                                                {Array.from({ length: 6 }).map((_, index) => (
                                                    <div key={index}>
                                                        <div className="h-4 bg-gray-200 rounded animate-pulse w-24 mb-2"></div>
                                                        <div className="h-10 bg-gray-200 rounded animate-pulse w-full"></div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </main>
                    </div>
                </div>
            </Layout>
        )
    }

    if (!data) {
        return (
            <Layout title="Hotel Booking" pageTag={"wakanow"} current={'/wakanow/hotel-bookings'}>
                <div className="text-center py-12">
                    <p className="text-gray-500">Hotel booking not found</p>
                </div>
            </Layout>
        )
    }

    const statusClass = statusColors[data.bookingStatus?.toLowerCase()] || 'bg-gray-100 text-gray-600';
    const hotel = data.hotelDetails;

    return (
        <Layout title="Hotel Booking" pageTag={"wakanow"} current={'/wakanow/hotel-bookings'}>
            <React.Fragment>
                <div className="min-h-full">
                    <div className="flex flex-1 flex-col">
                        <BackButton />
                        <main className="flex-1">
                            <div className="py-6">
                                <div className="mx-auto max-w-7xl px-4 sm:px-6 md:px-8">
                                    <div className="bg-white shadow overflow-hidden sm:rounded-lg">
                                        {hotel?.hotelImage && (
                                            <div className="relative h-64 sm:h-80 -mx-0 -mt-0 overflow-hidden">
                                                <img
                                                    src={hotel.hotelImage}
                                                    alt={hotel.hotelName || 'Hotel'}
                                                    className="w-full h-full object-cover"
                                                    onError={(e) => { e.target.style.display = 'none'; }}
                                                />
                                                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                                                <div className="absolute bottom-6 left-6 text-white">
                                                    <h2 className="text-2xl font-bold">{hotel.hotelName}</h2>
                                                    <p className="text-sm text-white/80 mt-1">
                                                        {[hotel.cityName, hotel.countryName].filter(Boolean).join(', ')}
                                                    </p>
                                                </div>
                                            </div>
                                        )}

                                        <div className="px-4 py-5 sm:p-6">
                                            <div className="flex items-center justify-between mb-6">
                                                <div>
                                                    <h2 className="text-xl font-bold text-gray-900">{data.bookingId}</h2>
                                                    <p className="text-sm text-gray-500 mt-1">Hotel Booking</p>
                                                </div>
                                                <span className={`px-3 py-1 text-sm font-semibold rounded-full shadow-sm ${statusClass}`}>
                                                    {data.bookingStatus}
                                                </span>
                                            </div>

                                            {hotel && !hotel.hotelImage && (
                                                <div className="mb-6">
                                                    <h2 className="text-xl font-bold text-gray-900">{hotel.hotelName}</h2>
                                                    <p className="text-sm text-gray-500 mt-1">
                                                        {[hotel.cityName, hotel.countryName].filter(Boolean).join(', ')}
                                                    </p>
                                                </div>
                                            )}

                                            <div className="grid grid-cols-1 gap-x-6 gap-y-6 sm:grid-cols-2 mb-8">
                                                <div className="bg-gray-50 rounded-lg p-4">
                                                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Amount</p>
                                                    <p className="mt-1 text-lg font-bold text-gray-900">{FormatMoney(data.amount, "NGN")}</p>
                                                </div>
                                                <div className="bg-gray-50 rounded-lg p-4">
                                                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Transfer Reference</p>
                                                    <p className="mt-1 text-sm font-medium text-gray-900 break-all">{data.transferReference || 'N/A'}</p>
                                                </div>
                                                <div className="bg-gray-50 rounded-lg p-4">
                                                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Transfer Status</p>
                                                    <p className="mt-1 text-sm font-medium text-gray-900">{data.transferStatus || 'N/A'}</p>
                                                </div>
                                                <div className="bg-gray-50 rounded-lg p-4">
                                                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Payment Status</p>
                                                    <p className="mt-1 text-sm font-medium text-gray-900">{data.paymentStatus || 'N/A'}</p>
                                                </div>
                                                <div className="bg-gray-50 rounded-lg p-4">
                                                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Hotel Confirmation</p>
                                                    <p className="mt-1 text-sm font-medium text-gray-900">{data.hotelBookingConfirmation || 'N/A'}</p>
                                                </div>
                                                <div className="bg-gray-50 rounded-lg p-4">
                                                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Reversal Status</p>
                                                    <p className="mt-1 text-sm font-medium text-gray-900">{data.reversalStatus || 'N/A'}</p>
                                                </div>
                                                <div className="bg-gray-50 rounded-lg p-4 sm:col-span-2">
                                                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Session Email</p>
                                                    <p className="mt-1 text-sm font-medium text-gray-900">{data.sessionEmail || 'N/A'}</p>
                                                </div>
                                                <div className="bg-gray-50 rounded-lg p-4">
                                                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Date Created</p>
                                                    <p className="mt-1 text-sm font-medium text-gray-900">{data.dateCreated ? convert(data.dateCreated) : 'N/A'}</p>
                                                </div>
                                                <div className="bg-gray-50 rounded-lg p-4">
                                                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Date Modified</p>
                                                    <p className="mt-1 text-sm font-medium text-gray-900">{data.dateModified ? convert(data.dateModified) : 'N/A'}</p>
                                                </div>
                                            </div>

                                            {hotel && (
                                                <div>
                                                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Hotel Details</h3>
                                                    <div className="grid grid-cols-1 gap-x-6 gap-y-6 sm:grid-cols-2 lg:grid-cols-4">
                                                        <div className="bg-gray-50 rounded-lg p-4">
                                                            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Check-in</p>
                                                            <p className="mt-1 text-sm font-medium text-gray-900">{hotel.checkin ? convert(hotel.checkin) : 'N/A'}</p>
                                                        </div>
                                                        <div className="bg-gray-50 rounded-lg p-4">
                                                            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Check-out</p>
                                                            <p className="mt-1 text-sm font-medium text-gray-900">{hotel.checkOut ? convert(hotel.checkOut) : 'N/A'}</p>
                                                        </div>
                                                        <div className="bg-gray-50 rounded-lg p-4">
                                                            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Nights</p>
                                                            <p className="mt-1 text-sm font-medium text-gray-900">{hotel.nights ?? 'N/A'}</p>
                                                        </div>
                                                        <div className="bg-gray-50 rounded-lg p-4">
                                                            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Rooms</p>
                                                            <p className="mt-1 text-sm font-medium text-gray-900">{hotel.numberOfRooms ?? 'N/A'}</p>
                                                        </div>
                                                        <div className="bg-gray-50 rounded-lg p-4">
                                                            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Adults</p>
                                                            <p className="mt-1 text-sm font-medium text-gray-900">{hotel.adultCount ?? 'N/A'}</p>
                                                        </div>
                                                        <div className="bg-gray-50 rounded-lg p-4">
                                                            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Children</p>
                                                            <p className="mt-1 text-sm font-medium text-gray-900">{hotel.childCount ?? 'N/A'}</p>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </main>
                    </div>
                </div>
            </React.Fragment>
        </Layout>
    )
}

export default ViewHotelBooking
